// Kırılım hiyerarşisi kopya kaynağı (spec 2026-10-30 §6 + §18 DR1–DR15) — TEK kaynak; `KPI_COPY.
// breakdown` ve `KPI_COPY.portal.breakdown` olarak da açılır (kpi-unit-copy.ts kalıbı; kpi-ui.ts
// 600 satırı aştığı için ayrı dosya). Danışman yüzeyi "kırılım / hesap grubu" dilini, müşteri
// (portal) yüzeyi "alt kalem" dilini kullanır: portal metinleri set/grup adı, "AI", "üretim",
// "danışman notu" taşımaz (kpi-breakdown-copy.test.ts kilidi). e2e 15/15b/15c `COPY` blokları
// elle eşlenik kopyadır — birini değiştirince diğerini de güncelle.

import { type KPI_BREAKDOWN_ERROR_CODES } from "@mahir/shared";
import { KPI_DIMENSIONS_COPY } from "./kpi-breakdown-dimension-copy";
import { possessiveSuffixTr } from "./report-import-ui";
import { SUPERADMIN_ONLY_HINT } from "./role-ui";

export type KpiBreakdownErrorCode = (typeof KPI_BREAKDOWN_ERROR_CODES)[number];

/** DR4 — toplam hücresinin dört "hesaplanamıyor / yedek" durumu (asla kırmızı). */
export type KpiBreakdownTotalState =
  | "member_missing_in_set"
  | "member_value_missing"
  | "calc_group_other_set"
  | "reported_fallback";

/** "2 kalemden 1'i" — Türkçe iyelik eki sayıdan (report-import-ui `possessiveSuffixTr`). */
const ofItems = (part: number, total: number): string =>
  `${total.toString()} kalemden ${part.toString()}${possessiveSuffixTr(part)}`;

/** DR5 — hata kodu → danışman metni (spec §18 tablosu; eksik kod derlemede patlar). */
const ERRORS: Record<KpiBreakdownErrorCode, string> = {
  duplicate_dimension: "Bu göstergede aynı boyutta bir kırılım grubu zaten var",
  duplicate_member: "Bu grupta aynı üye zaten var",
  breakdown_member_client_mismatch: "Müşteriye özel kalem yalnız o müşterinin setine eklenebilir",
  member_requires_group: "Alt kalemler bir kırılım grubu altında olmalı",
  group_under_category: "Alt kalemler bir kırılım grubu altında olmalı",
  breakdown_on_non_group: "Alt kalemler bir kırılım grubu altında olmalı",
  breakdown_depth: "En fazla iki seviye kırılım açılabilir",
  breakdown_member_move: "Üye grubundan, grup göstergesinden ayrılamaz",
  breakdown_member_parent:
    "Kırılım grubu ve üyeleri sete yalnız kendi üst göstergesinin altına eklenebilir",
  breakdown_group_conflict: "Formüldeki grup bu sette hesap grubu olamaz",
  calculation_group_incomplete: "Seçili kırılımlar hesap grubu olamaz",
  breakdown_group_missing: "Hesap grubu bu sette yok",
  breakdown_member_missing: "Kırılımın bazı kalemleri sette yok",
  breakdown_mismatch: "Grup toplamı üst değerle uyuşmuyor",
  legacy_metric_children: "Bu gösterge altında grupsuz alt satırlar var",
  KPI_GROUP_READONLY: "Grup satırı değer almaz",
  KPI_DERIVED_READONLY: "Toplam alt kalemlerden hesaplanır; raporlanan değer ekleyebilirsiniz",
  KPI_NODE_KIND_IN_USE: "Değeri olan gösterge kategori/gruba çevrilemez",
  breakdown_convert_category_has_values:
    "Bu kategoride değer var — 'Bu kategoriyi toplam göstergesine dönüştür'ü seçin",
  breakdown_unit_mismatch: "Alt kalemlerin birimi üst göstergeden farklı",
  unit_change_on_breakdown_child: "Birim üst göstergeden değiştirilir",
};

/** Hata kodunun eylem düğmesi (spec §18 "Eylem" sütunu; yalnız düğmesi olanlar). */
const ERROR_ACTIONS: Partial<Record<KpiBreakdownErrorCode, string>> = {
  member_requires_group: "+ Kırılım grubu",
  group_under_category: "+ Kırılım grubu",
  breakdown_on_non_group: "+ Kırılım grubu",
  breakdown_group_missing: "Kırılımı bu sete ekle",
  breakdown_member_missing: "Seti düzenle",
  legacy_metric_children: "Kırılıma dönüştür",
  KPI_DERIVED_READONLY: "Raporlanan değer ekle",
  breakdown_unit_mismatch: "Birim değişimine git",
};

/** Boyut kodu → hesap satırındaki kısa ad (shared `KPI_BREAKDOWN_DIMENSIONS`; "diger" grup etiketiyle). */
const DIMENSION_NAMES: Partial<Record<string, string>> = {
  cinsiyet: "cinsiyet",
  yaka: "yaka",
  yas_grubu: "yaş grubu",
  calisma_turu: "çalışma türü",
  sozlesme_turu: "sözleşme türü",
  bolge: "bölge",
  kademe: "kademe",
  egitim: "eğitim",
  atik_turu: "atık türü",
  enerji_kaynagi: "enerji kaynağı",
  kapsam: "kapsam",
};

/** Müşteri yüzeyi — tek genel kopyalar, "alt kalem" dili; set/grup adı YOK. */
const PORTAL = {
  /** grup bandı eki: `complete=false` grubu */
  partialGroup: "(seçili kalemler)",
  /** kapalı toplam satırı eki: üye sayısı (grup adı/sayısı değil) */
  collapsedCount: (members: number) => `· ${members.toString()} alt kalem`,
  collapsedCountNarrow: (members: number) => members.toString(),
  toggle: (open: boolean, label: string) =>
    open ? `${label} — alt kalemleri gizle` : `${label} — alt kalemleri göster`,
  /** üye ad hücresi `aria-label` yolu ayırıcısı (danışmanla aynı) */
  pathSeparator: " › ",
  totalAria: (label: string, members: number) =>
    `${label} — ${members.toString()} alt kalemden hesaplanır`,
  /** canlı bölge (toplam hücresi, `aria-live`): "Toplam çalışan sayısı 2025: 1.235" */
  live: (label: string, column: string, value: string) => `${label} ${column}: ${value}`,
  autoComputed: "Bu değer alt kalemlerden otomatik hesaplanır",
  /** bekleme (editör başlığı, `aria-busy`) */
  recomputing: "Toplam yeniden hesaplanıyor…",
  waitMembers: "Alt kalemler tamamlanınca hesaplanır",
  mismatch: "Alt kalemlerin toplamı üst değerle uyuşmuyor",
  readonlyRow: "Bu satır değer almaz",
  /** DR4 hücre metinleri (soluk, kırmızı değil) */
  states: {
    member_missing_in_set: "Bu değer alt kalemlerden otomatik hesaplanır",
    member_value_missing: (entered: number, total: number) =>
      `— · ${ofItems(entered, total)} girildi`,
    calc_group_other_set: "Bu değer alt kalemlerden otomatik hesaplanır",
    reported_fallback: "Bu değer alt kalemlerden otomatik hesaplanır",
  },
  /** hata kodu → müşteri metni (spec §18 "Portal" sütunu); listede olmayan kod `errorFallback` */
  errors: {
    breakdown_group_missing: "Bu değer alt kalemlerden otomatik hesaplanır",
    breakdown_member_missing: "Alt kalemler tamamlanınca hesaplanır",
    breakdown_mismatch: "Alt kalemlerin toplamı üst değerle uyuşmuyor",
    KPI_GROUP_READONLY: "Bu satır değer almaz",
    KPI_DERIVED_READONLY: "Bu değer alt kalemlerden otomatik hesaplanır",
  } satisfies Partial<Record<KpiBreakdownErrorCode, string>>,
  errorFallback: "Bu işlem yapılamadı — değer kaydedilmedi",
} as const;

const MODAL_ROLLUP_SHORT = { sum: "toplanır", avg: "ortalanır", none: "hesaplanmaz" } as const;

/** DR6 — BreakdownGroupModal (Modal.dc.html sol panel; jargon yok: sum/avg/slug/"tamlık"). */
const MODAL = {
  kicker: "Kırılım",
  titleNew: "Kırılım grubu ekle",
  titleMembers: (group: string) => `Üye ekle — ${group}`,
  scopeShared: "Kapsam: ortak katalog",
  scopeSharedRest: "— bu grubu taşıyan tüm müşteriler",
  scopeClient: "Kapsam: müşteriye özel",
  scopeClientRest: (client: string) => (client ? `— yalnız ${client}` : "— yalnız bu müşteri"),
  clientOnlyToggle: (client: string) =>
    `Yalnız ${client || "bu müşteri"} için oluştur (müşteriye özel)`,
  parentLine: "Üst gösterge:",
  parentUnit: (unit: string) => `· birim ${unit}`,
  /** İkinci seviye: grup bir ÜYENİN altına kurulur — "Üst: Mavi yaka (Yaka grubu) · Toplam Çalışan" */
  parentLineNested: (member: string, group: string, root: string) =>
    `Üst: ${member} (${group}) · ${root}`,
  dimension: "Boyut",
  dimensionPlaceholder: "Boyut seçin…",
  dimensionSearch: "Boyut ara…",
  dimensionUsed: "bu göstergede var",
  /** K-B6 — "Diğer boyutlar" açılışta daraltılır */
  showOther: (n: number) => `Diğer boyutları göster (${n.toString()})`,
  hideOther: "Diğer boyutları gizle",
  /** §19 B4 — seçicinin soru biçimli etiketi + tek satır açıklama (kicker yok) */
  dimensionQuestion: "Neye göre kırılıyor?",
  dimensionIntro:
    "Toplamı hangi ölçüte göre parçalayacağınızı seçin. Örn. Toplam çalışan → Cinsiyete göre: Kadın + Erkek.",
  /** §19 B3 — liste bölümleri */
  sections: {
    suitable: "Bu gösterge için uygun",
    other: "Diğer boyutlar",
    custom: "Sizin tanımladıklarınız",
  },
  exampleMembers: (list: string) => `Örnek üyeler: ${list}`,
  customHelp: "Daha önce tanımladığınız boyut.",
  customNoMembers: "Üye şablonu yok — üyeleri siz yazarsınız.",
  customNameLabel: "Boyut adı",
  customNamePlaceholder: "örn. Tesise göre",
  customSlugLabel: "Kod",
  needDimensionName: "Boyut adını yazın",
  showGroupLabel: "Raporda görünen adı değiştir",
  customSlugHint: "Boyut adından üretilir; küçük harf, rakam ve alt çizgi (en fazla 40).",
  similarDimension: (label: string) =>
    `Benzer boyut var: ${label} — onu seçin ya da yine de oluşturun`,
  useSimilar: "Onu seç",
  /** §19 B5 — elle düzenlenmiş üyelerin üzerine şablon yazmadan önce */
  replaceMembersConfirm: {
    title: "Alt kalemler değiştirilsin mi?",
    body: "Alt kalemleri elle düzenlediniz. Yeni boyutun örnek üyeleri bunların yerine geçer.",
    confirm: "Örnek üyelerle değiştir",
    cancel: "Vazgeç",
  },
  /** §19 B6 — canlı örnek cümle (üye etiketleri + üst etiket) */
  live: {
    lead: "Toplam şöyle hesaplanır:",
    /** başka bir grup toplamı zaten hesaplıyorsa: bu grup yalnız karşılaştırılır */
    check: (parts: string, total: string) => `Kontrol: ${parts}, ${total} ile karşılaştırılır`,
    sum: (members: string, total: string) => `${members} = ${total}`,
    avgParts: (members: string, count: number) => `(${members}) / ${String(count)}`,
    avg: (members: string, total: string, count: number) =>
      `(${members}) / ${String(count)} = ${total}`,
    none: "Yalnız gösterim — toplam elle girilir, alt kalemler kıyaslanmaz",
    emptyMembers: "… + …",
  },
  customDimension: "Boyut kodu",
  customDimensionHint: "Grup adından üretilir; küçük harf, rakam ve alt çizgi (en fazla 40).",
  customDimensionInvalid: "Boyut kodu küçük harfle başlamalı; yalnız a-z, 0-9 ve _ (en fazla 40)",
  codePreview: "Grup kodu:",
  groupLabel: "Grup adı",
  complete: "Alt kalemler toplamın tamamını kapsar",
  completeHint: "İşaretli değilse toplam bu gruptan hesaplanmaz, yalnız görüntülenir.",
  rollupLegend: "Hesaplama",
  rollup: { sum: "Topla", avg: "Ortala", none: "Hesaplama yok (yalnız gösterim)" },
  avgHint: "— oran göstergelerinde önerilmez (ağırlıksız)",
  avgExample: "Örnek: alt kalemler 40 ve 60 ise toplam 50 olur (ağırlıksız ortalama).",
  rateInfo:
    "Oranlar ağırlıksız ortalanamaz; toplamı ham girin ya da AGIRLIKLI_ORT ile formül yazın.",
  existingCalcInfo: (group: string) =>
    `Toplam “${group}” grubundan hesaplanıyor; bu grup doğrulama için.`,
  readonlyGroup: (complete: boolean, rollup: "sum" | "avg" | "none") =>
    `${complete ? "Alt kalemler toplamın tamamını kapsar" : "Seçili kırılımlar"} · ${MODAL_ROLLUP_SHORT[rollup]}`,
  members: "Alt kalemler",
  memberName: "Üye adı",
  memberPlaceholder: "örn. Kadın",
  fromCatalog: "Katalogdan seç",
  fromCatalogNone: "Katalogda bu gruba ait, sette olmayan kalem yok",
  removeMember: (name: string) => (name ? `${name} üyesini sil` : "Üyeyi sil"),
  addMember: "+ Üye",
  existingMembers: (names: string) => `Sette: ${names}`,
  needMembers: (n: number) => `En az ${n.toString()} üye gerekli`,
  needDimension: "Boyut seçin",
  needLabel: "Grup adı gerekli",
  duplicateMemberLabel: "Aynı adlı iki üye var",
  previewLead: "Kaydet'ten önce önizleme:",
  previewIdle: "değerlere etkisi bir sonraki adımda gösterilir.",
  previewValues: (n: number) =>
    `Bu müşteride ${n.toString()} toplam değeri raporlanan olarak korunacak.`,
  /** katalog bağlamı (müşterisiz): ortak grup tüm müşterileri etkiler */
  previewValuesShared: (clients: number, n: number) =>
    `${clients.toString()} müşteride ${n.toString()} toplam değeri raporlanan olarak korunacak.`,
  previewNoValues: "Raporlanan değere dönüşecek toplam değeri yok.",
  previewRows: (n: number) => `Sete eklenecek satır: ${n.toString()} (grup ve üyeler)`,
  cancel: "Vazgeç",
  back: "Geri",
  preview: "Önizle ve kaydet",
  save: "Kaydet",
  previewing: "Hesaplanıyor…",
  saving: "Kaydediliyor…",
  success: (group: string, members: number) =>
    `Kırılım grubu eklendi: ${group} (${members.toString()} üye)`,
  successMembers: (group: string, members: number) => `${group}: ${members.toString()} üye eklendi`,
} as const;

/** DR3/DR6 — IndicatorModal "Kırılım" bölümü + hesap grubu radyosu (Modal.dc.html sağ panel). */
const CALC_GROUP = {
  kicker: "Gösterge · Kırılım",
  summary: (label: string, groups: number) => `${label} · ${groups.toString()} grup`,
  legend: "Toplam nereden hesaplanır",
  auto: "Otomatik — şu an:",
  autoNone: "yok (toplam ham girilir)",
  reasonPartial: "seçili kırılımlar, hesap grubu olamaz",
  reasonNone: "yalnız karşılaştırma, hesap grubu olamaz",
  reasonEmpty: "üye yok, hesap grubu olamaz",
  locked: "formül belirliyor",
  /** "Formülde `TOPLA_ALT("cinsiyet")` varsa bu seçim kilitlenir: formül belirliyor." */
  lockLead: "Formülde",
  lockIf: "varsa bu seçim kilitlenir:",
  lockActive: "var — bu seçim kilitli:",
  exampleCall: (dimension: string) => `TOPLA_ALT("${dimension}")`,
  roleCheck: "· doğrulama",
  membersLine: (names: string, complete: boolean) =>
    `${names || "üye yok"} · ${complete ? "tam" : "seçili kırılımlar"}`,
  editInCatalog: "Katalogda düzenle",
  addGroup: "+ Kırılım grubu ekle",
  footerLead: "Üyede birim seçici yok:",
  footerInherit: (unit: string) => `birim üst göstergeden devralınır (${unit || "—"})`,
  footerTail: ". Grup sıralaması hesabı değiştirmez.",
  memberInfo: (parent: string, group: string) => `Üst gösterge: ${parent} · grup: ${group}`,
} as const;

/** DR15 — SetTreeEditor kırılım eylemleri ve satır türleri. */
const TREE = {
  pillPrefix: "kırılım:",
  menu: (label: string) => `${label} — diğer eylemler`,
  menuButton: "⋯",
  addGroup: "+ Kırılım grubu ekle",
  addMember: "+ Üye",
  removeGroup: "Veri setinden kaldır",
  emptyGroup: "Üye yok — + Üye ekle",
  inheritedUnit: "(üstten)",
  memberHidesTotal: "üye gizli — toplam portalda gizlenecek",
  calcGroupChange: (before: string, after: string) => `hesap grubu değişiyor: ${before} → ${after}`,
  noCalcGroup: "yok",
  /** TODO-KPI-116 — 0069 sonrası değerli kategori satırı */
  legacyValues: "Bu satırda eski değerler var; toplam göstergesi yapmak için Kırılıma dönüştür",
  legacyValuesAction: "Kırılıma dönüştür",
  /** TODO-KPI-151 — sıralama sonrası NULL kuralıyla hesap grubu değişti */
  calcGroupChanged: (total: string, from: string, to: string) => `${total}: ${from} → ${to}`,
  calcGroupChangedLead: "Hesap grubu değişti (sıralama sonrası ilk tam grup):",
  /** TODO-KPI-111 — eksik üye hücre ipucu */
  missingMember: (label: string) => `${label} (sette yok)`,
  missingCount: (n: number, total: number) => `${ofItems(n, total)} sette`,
} as const;

/** AddIndicatorPicker — kırılımlı gösterge seçimi (§6.3). */
const PICKER = {
  autoChecked: "Kırılım grupları ve üyeleri otomatik işaretlendi; tek tek kaldırabilirsiniz.",
  partialHint:
    "Kısmi seçimde grup bu sette eksik kalır; toplam, eksik üyeler sete alınınca hesaplanır.",
  emptyGroupSkipped: (n: number) => `${n.toString()} üyesiz grup sete alınmayacak`,
} as const;

/** DR14 — MetricModal "Konum" seçicisi (Modal.dc.html radyo düzeni; spec §6.4). */
const LOCATION = {
  legend: "Konum",
  category: "Bir kategori altında",
  member: "Bir göstergenin alt kalemi",
  root: "Kök (ana kategori)",
  rootHint: "katalog sekmesi olur",
  categoryLabel: "Üst kategori",
  categorySearch: "Kategori adı ya da kodu…",
  categoryPlaceholder: "Kategori seçin…",
  totalLabel: "Üst gösterge",
  totalPlaceholder: "Gösterge seçin…",
  totalSearch: "Gösterge adı, kodu ya da yolu…",
  /** ilk 50 sonuçtan fazlası varsa listenin sonunda */
  narrow: (shown: number, total: number) =>
    `İlk ${shown.toString()} / ${total.toString()} sonuç — aramayı daraltın`,
  groupLegend: "Kırılım grubu",
  newGroup: "Yeni grup…",
  noGroups: "Bu göstergede henüz kırılım grubu yok — “Yeni grup…” ile ekleyin.",
  memberSlug: "Üye kodu",
  memberSlugHint: "Etiketten üretilir; küçük harf, rakam ve alt çizgi (en fazla 64).",
  memberSlugInvalid: "Üye kodu küçük harfle başlamalı; yalnız a-z, 0-9 ve _ (en fazla 64)",
  unitLabel: "Varsayılan birim",
  unitInherited: (unit: string) => `${unit || "—"} ${TREE.inheritedUnit}`,
  unitInheritedHint: "Alt kalemin birimi üst göstergeden devralınır; birim üstten değiştirilir.",
  memberLocked: "Üye grubundan, grup göstergesinden ayrılamaz",
  needTotal: "Üst gösterge seçin",
  needGroup: "Üst gösterge ve kırılım grubu seçin",
  needCategory: "Üst kategori seçin",
  rootLocked: `Kök: ${SUPERADMIN_ONLY_HINT}`,
  memberDepth: "Alt göstergesi olan gösterge alt kalem olamaz",
  groupParent: (total: string) => `Üst gösterge: ${total}`,
} as const;

/** Katalog tablosu + MetricModal "Kırılım" paneli (spec §6.4, DR7/DR14). */
const CATALOG = {
  convertAction: "Kırılıma dönüştür",
  convertAria: (label: string) => `${label} — kırılıma dönüştür`,
  groupKicker: "Kırılım grubu",
  panelTitle: "Kırılım",
  panelNote:
    "Katalogda formül yazılmaz: bu göstergeyi taşıyan veri setlerinde üyeler sete alınınca toplam otomatik hesaplanır.",
  panelEmpty: "Kırılım grubu yok.",
  groupLine: (members: number, complete: boolean, rollup: "sum" | "avg" | "none") =>
    `${members.toString()} üye · ${MODAL.readonlyGroup(complete, rollup)}`,
  addGroup: "+ Kırılım grubu ekle",
  /** Panelde seviye-1 üye yanındaki düğmenin erişilebilir adı (K-B9) */
  addGroupUnderMember: (member: string) => `+ Kırılım grubu ekle — ${member}`,
  dimensionLine: (dimension: string) => `Boyut: ${dimension}`,
  metaPreviewing: "Etkisi hesaplanıyor…",
  metaPreview: (clients: number, values: number) =>
    values > 0
      ? `Kaydedince ${clients.toString()} müşteride ${values.toString()} toplam değeri raporlanan olarak korunacak.`
      : MODAL.previewNoValues,
  metaPreviewFailed: "Etkisi hesaplanamadı — kaydedince sunucu yeniden denetler.",
  /** tam grup "seçili kırılımlar"a dönerken (hesap grubu adaylığı düşer) */
  completeOffWarning: "Bu grubu hesap grubu olarak kullanan setlerde seçim 'Otomatik'e döner",
} as const;

/** DR6 — "Kırılıma dönüştür" üç adımlı sihirbaz (spec §6.4). */
const CONVERT = {
  kicker: "Kırılıma dönüştür",
  title: (label: string) => `Kırılıma dönüştür — ${label}`,
  steps: ["Kalıp ve hedef", "Neye göre ve hangi kalemler", "Önizleme"] as const,
  stepLine: (n: number, name: string) => `Adım ${n.toString()}/3 · ${name}`,
  patternCategory: "Kategorinin alt göstergeleri bir toplam göstergesinin kırılımı olur.",
  patternSiblings: "Aynı kategorideki göstergelerden bazıları bu toplamın alt kalemi olur.",
  targetLegend: "Toplam göstergesi",
  newTotal: "Bu kategoriyi toplam göstergesine dönüştür",
  newTotalHint: "Kategori satırının kendisi toplam olur; değerleri, notları ve formülleri korunur.",
  siblingTargetHint: "Kategori, alt göstergeleri bu toplama taşındıktan sonra silinir.",
  suggested: "önerilen — formülü bu kategorinin toplamı",
  totalFixed: (label: string) => `Toplam: ${label}`,
  membersLegend: "Alt kalemler",
  siblingsLegend: "Alt kalem olacak göstergeler",
  siblingsHint:
    "Aynı kategorideki hangi göstergeler bu toplamın alt kalemi olsun? İşaretlenenler toplamın altına taşınır; sağdaki ad raporda alt kalem adı olarak görünür.",
  memberNameHeader: "Alt kalem adı",
  memberLabelAria: (label: string) => `${label} — alt kalem adı`,
  ineligible: {
    unit: (unit: string | null) => (unit ? `birimi farklı (${unit})` : "birimi farklı"),
    valueType: "değer tipi farklı",
    derived: "hesaplanan gösterge alt kalem olamaz",
    breakdown: "kendi kırılımı var",
  },
  /** TODO-KPI-157(2): menüde pasif eylemin nedeni */
  noEligibleSibling: (why: string) =>
    why === ""
      ? "Alt kalem olabilecek kardeş gösterge yok"
      : `Alt kalem olabilecek kardeş yok — ${why}`,
  needDimension: "Devam için neye göre kırıldığını seçin",
  arrow: "→",
  needSiblings: "En az bir alt kalem seçin",
  needMemberLabel: "Alt kalem adları boş olamaz",
  scanning: (sets: number | null) =>
    sets !== null && sets > 0 ? `${sets.toString()} set taranıyor…` : "Veri setleri taranıyor…",
  counts: (sets: number, formulas: number, values: number) =>
    `${sets.toString()} set · ${formulas.toString()} formül · ${values.toString()} değer raporlanan olacak`,
  before: "Önce",
  after: "Sonra",
  untouchedLead: "Dokunulmayan formüller — elle gözden geçirin:",
  untouchedNone: "Dokunulmayan formül yok.",
  untouchedScope: (setId: string | null) => (setId ? "veri seti" : "katalog"),
  irreversible: "Geri alınamaz — dönüşüm tek işlemde uygulanır.",
  noop: "Bu dönüşüm zaten yapılmış — değişiklik yok.",
  next: "İleri",
  back: "Geri",
  cancel: "Vazgeç",
  previewing: "Hesaplanıyor…",
  commit: "Dönüştür",
  committing: "Dönüştürülüyor…",
  done: "Tamam",
  success: (group: string) => `Kırılıma dönüştürüldü: ${group}`,
  successCounts: (sets: number, formulas: number, values: number) =>
    `${sets.toString()} set · ${formulas.toString()} formül güncellendi · ${values.toString()} değer raporlanan değere dönüştü`,
  hasValues: {
    values: ERRORS.breakdown_convert_category_has_values,
    notes: "Bu kategoride not var — 'Bu kategoriyi toplam göstergesine dönüştür'ü seçin",
    formulas:
      "Bu kategoriye başka formüller başvuruyor — 'Bu kategoriyi toplam göstergesine dönüştür'ü seçin",
  },
  switchToNewTotal: "Bu seçeneğe geç",
  unitMismatch: (unit: string | null) =>
    unit ? `${ERRORS.breakdown_unit_mismatch} (${unit})` : ERRORS.breakdown_unit_mismatch,
  goToUnit: "Birim değişimine git",
  forbidden: `Bu dönüşüm bir katalog kategorisine dokunuyor — ${SUPERADMIN_ONLY_HINT}`,
} as const;

/** Set Yönetimi katalog bandı — yeni grup/üye önerisi (spec §6.4 son madde, mevcut new_metric yolu). */
const SYNC = {
  kindGroup: "Yeni kırılım grubu",
  kindMember: "Yeni kırılım üyesi",
  group: (total: string, label: string) => `${total} göstergesine yeni kırılım (grup): ${label}`,
  member: (total: string, label: string) => `${total} göstergesine yeni kırılım (üye): ${label}`,
  hintGroup: "Kırılım grubu toplam göstergesinin altına veri setine eklenir; değer girilmez.",
  hintMember: "Üye, kırılım grubunun altına veri setine eklenir; değer girilmez.",
} as const;

export const KPI_BREAKDOWN_COPY = {
  // ─── Grid satır türleri (DR1/DR2/DR8/DR9) ──────────────────────────────────
  /** grup bandı rol eki — yalnız danışman, yalnız hesap grubu */
  calcGroupSuffix: "· hesap grubu",
  /** grup bandı eki: `complete=false` grubu */
  partialGroup: "(seçili kırılımlar)",
  /** kapalı toplam satırı eki (DR2): "· 2 kırılım"; dar düzende yalnız sayı */
  collapsedCount: (groups: number) => `· ${groups.toString()} kırılım`,
  collapsedCountNarrow: (groups: number) => groups.toString(),
  /** toplam satırının aç/kapa düğmesi `aria-label` */
  toggle: (open: boolean, label: string) =>
    open ? `${label} — kırılımları gizle` : `${label} — kırılımları göster`,
  /** üye ad hücresi `aria-label`: "Toplam çalışan sayısı › Cinsiyete göre › Kadın" */
  pathSeparator: " › ",
  /** toplam ad hücresi `aria-label`: tam grup listesi (kapalıyken görünen yalnız sayı) */
  totalAria: (label: string, groups: readonly string[]) =>
    groups.length > 0 ? `${label} — kırılımlar: ${groups.join(", ")}` : label,
  /** canlı bölge (toplam hücresi, `aria-live`): "Toplam çalışan sayısı 2025: 1.235" */
  live: (label: string, column: string, value: string) => `${label} ${column}: ${value}`,
  /** toplam hücresindeki amber nokta (DR1 — uyuşmazlık bantta değil hücrede) */
  mismatchDot: "Grup toplamı üst değerle uyuşmuyor",
  /** Set editörü / katalog tek biçim rozeti (grid'de ve portalda YOK): "cinsiyet (2) · yaş (3)" */
  pill: (groups: readonly { label: string; members: number }[]) =>
    groups.map((g) => `${g.label} (${g.members.toString()})`).join(" · "),
  /** AddIndicatorPicker — kısmi set uyarısı */
  missingMembers: (n: number) => `kırılımın ${n.toString()} üyesi sette yok`,
  /**
   * SetSettingsPanel — `kpi_sets.report_breakdowns` anahtarı (§6.7; varsayılan açık). Bugün yalnız
   * DOCX okur (Excel kırılım satırlarını her zaman yazar) — metin buna göre; Excel de uygularsa
   * "Word ve Excel raporunda" olur.
   */
  reportToggle: "Raporda kırılımları göster",
  reportToggleHelp: "Kapalıyken Word raporunda yalnız toplamlar yer alır",
  reportToggleFailed: "Rapor ayarı kaydedilemedi — tekrar deneyin",

  // ─── Toplam hücresi durum tablosu (DR4 — Editor.dc.html metinleri) ─────────
  states: {
    member_missing_in_set: {
      title: "Üye sette eksik",
      cell: (inSet: number, total: number) => `${ofItems(inSet, total)} sette`,
      /** sayı bilinmiyorsa (yalnız `failure` geldi) */
      cellGeneric: "kırılımın bazı kalemleri sette yok",
      info: "Toplam, eksik üye sete alınınca hesaplanır.",
      action: "Seti düzenle",
    },
    member_value_missing: {
      title: "Üye değeri eksik",
      cell: (entered: number, total: number) => `— · ${ofItems(entered, total)} girildi`,
      cellGeneric: "alt kalemler bekleniyor",
      info: "Alt kalemler tamamlanınca hesaplanır.",
      action: (label: string, column: string) => `Hücreye git: ${label} ${column}`,
    },
    calc_group_other_set: {
      title: "Hesap grubu bu sette yok",
      cell: "başka sette hesaplanıyor",
      info: "Bu gösterge başka bir veri setinde kırılımdan hesaplanıyor.",
      infoReported: "Raporlanan değer varsa onun yanında RAPORLANAN rozeti görünür.",
      action: "Kırılımı bu sete ekle",
    },
    reported_fallback: {
      title: "Raporlanan yedek",
      badgeTitle: "raporlanan · kırılım eklendi",
      info: "Hesaplanan değer farklı çıkarsa fark raporlanan değer penceresinde görünür.",
      action: "Raporlanan değer ekle",
    },
  } satisfies Record<KpiBreakdownTotalState, object>,
  /** iyimser kayıt sırasında / dryRun düğmeleri */
  waiting: "Hesaplanıyor…",
  /** kayıt hatası bandı */
  saveFailed: "Kaydedilemedi — hiçbir değer değişmedi",
  /** grup eklenince kalıcı bilgi bandı (kaybolan bildirim değil) */
  convertedBand: (n: number) =>
    `${n.toString()} değer raporlanan değere dönüştü; üyeler girilince toplam hesaplanır.`,
  convertedBandClose: "Kapat",
  /** hücre editörü — toplam hesap satırı: "Kırılımdan hesaplanıyor (cinsiyet): Kadın 222 + …" */
  computedFrom: (dimension: string, parts: string) =>
    `Kırılımdan hesaplanıyor (${dimension}): ${parts}`,
  /** hücre editörü — hesap vermeyen grubun bilgi satırı (ham üst) */
  rawTotalInfo: (dimension: string, value: string) => `Kırılım toplamı (${dimension}): ${value}`,
  /** hücre editörü — üye hücresinin alt satırı */
  memberInfo: (parent: string, value: string, group: string) =>
    `Üst: ${parent} → ${value} · grup: ${group}`,
  /** hücre editörü — hesap grubu dışı tam grubun doğrulama satırı */
  checkLine: (group: string, value: string, ok: boolean) => `${group}: ${value}${ok ? " ✓" : ""}`,
  /** `complete=false` grup — karşılaştırılmaz */
  checkLinePartial: (group: string, value: string) =>
    `${group}: ${value} (seçili kırılımlar — karşılaştırılmaz)`,
  /** `complete=true ∧ rollup='none'` grup — hesaplama yok, değer gösterilmez, karşılaştırılmaz */
  checkLineDisplayOnly: (group: string) => `${group} (yalnız gösterim — karşılaştırılmaz)`,
  /** tam grup toplamı üst değerle uyuşmuyor (amber, bloklamaz): "Yaşa göre: ✗ 1.230 (fark -5)" */
  checkLineMismatch: (group: string, value: string, diff: string) =>
    `${group}: ✗ ${value} (fark ${diff})`,
  /** hesap/doğrulama satırında boş üye değeri */
  emptyValue: "—",
  /** hesap satırında sette olmayan beklenen üyeler (adları DTO'da yok) */
  notInSetPart: "eksik kalem (sette yok)",
  /** boyut kodu → hesap satırındaki kısa ad ("cinsiyet", "yaş grubu"); bilinmeyen → grup etiketi */
  dimensionName: (dimension: string | undefined, groupLabel: string) =>
    (dimension ? DIMENSION_NAMES[dimension] : undefined) ?? groupLabel,
  /** toplam/grup hücresine yazma denemesi (grid ipucu + sunucu KPI_DERIVED/GROUP_READONLY) */
  readonlyHint: (group: string | null) =>
    group
      ? `Bu değer alt kalemlerden otomatik hesaplanır (${group}) — raporlanan değer ekleyebilirsiniz`
      : "Bu değer alt kalemlerden otomatik hesaplanır — raporlanan değer ekleyebilirsiniz",
  /** "Form yanıtını al" türetilmiş toplamda pasif — düğme `title` */
  formLinkDerived: "Toplam alt kalemlerden hesaplanır — form yanıtını alt kalemlere girin",
  /** bekleme: üye kaydı sonrası toplam yeniden hesaplanırken (editör başlığı, `aria-busy`) */
  recomputing: "Toplam yeniden hesaplanıyor…",
  /** DR5 — sayaç şeridi adları (Özet durum şeridi; her biri kendi süzgecini açar) */
  counters: {
    indicators: (n: number) => `${n.toString()} gösterge`,
    missingCells: (n: number) => `Veri bekleyen hücre: ${n.toString()}`,
    approvalCells: (n: number) => `Onay bekleyen hücre: ${n.toString()}`,
    calcErrorTotals: (n: number) => `Hesaplanamayan toplam: ${n.toString()}`,
    /** aynı süzgecin Özet sekme etiketi (sayı `(n)` sekme düğmesinde) */
    calcErrorTab: "Hesaplanamayan toplam",
  },

  // ─── Hata kodu → metin (DR5) ────────────────────────────────────────────────
  errors: ERRORS,
  errorActions: ERROR_ACTIONS,
  /** `breakdown_group_conflict` sunucu nedeniyle: "…: <neden>" */
  groupConflict: (reason: string) => `${ERRORS.breakdown_group_conflict}: ${reason}`,
  /** `breakdown_mismatch` ayrıntılı (AI kontrolü / hücre editörü) */
  mismatchDetail: (group: string, groupValue: string, totalValue: string) =>
    `${ERRORS.breakdown_mismatch} (${group}: ${groupValue} ≠ ${totalValue})`,
  /** `duplicate_dimension` / `duplicate_member` adlı biçim (modal satır içi hata) */
  duplicateDimension: (group: string) => `Bu göstergede '${group}' grubu zaten var`,
  duplicateMember: (member: string) => `Bu grupta '${member}' üyesi zaten var`,
  /** listede olmayan sunucu kodu */
  errorFallback: "Kırılım işlemi yapılamadı",

  modal: MODAL,
  dimensions: KPI_DIMENSIONS_COPY,
  calcGroup: CALC_GROUP,
  tree: TREE,
  picker: PICKER,
  location: LOCATION,
  catalog: CATALOG,
  convert: CONVERT,
  sync: SYNC,

  portal: PORTAL,
} as const;

/** Sunucu hata kodu → danışman metni (bilinmeyen kod → sunucu mesajı ya da genel metin). */
export function breakdownErrorText(code: string, serverMessage?: string): string {
  return (
    (KPI_BREAKDOWN_COPY.errors as Record<string, string | undefined>)[code] ??
    serverMessage ??
    KPI_BREAKDOWN_COPY.errorFallback
  );
}

/** Sunucu hata kodu → müşteri metni (tek genel kopya; sunucu mesajı asla gösterilmez). */
export function portalBreakdownErrorText(code: string): string {
  return (PORTAL.errors as Record<string, string | undefined>)[code] ?? PORTAL.errorFallback;
}
